import React from 'react';

import { Sheet, useSheetDismiss } from '@/nova/components/Sheet';

import { SUBJECT_ART, VIDEO_SUBJECT_TYPES, type VideoSubjectType } from '../../video-subjects';
import * as S from './VideoSubjectPicker.styles';

/**
 * Categories offered in the chooser. `gamification` is HIDDEN for now: there is no good source visual to feed
 * the videos yet, so it is not an option to START from (the flow itself still exists for reopening). Drop the
 * filter to bring it back.
 */
const OFFERED_SUBJECT_TYPES: readonly VideoSubjectType[] = VIDEO_SUBJECT_TYPES.filter((t) => t !== 'gamification');

export interface VideoSubjectPickerProps {
  /** Called with the chosen category; the parent then opens the matching picker. */
  onPick: (subject: VideoSubjectType) => void;
  /** Dismiss without choosing (backdrop / Escape / close) — the parent returns to the library. */
  onClose: () => void;
  /** The categories offered, in order. Defaults to the video-ad set (gamification hidden); an ad kind with a source
   *  visual for every category (banner kits) passes all three. */
  subjectTypes?: readonly VideoSubjectType[];
}

/**
 * The first step of a NEW video ad: pick what to advertise. Three cards, each with its illustration
 * bleeding from the top-right and a minimal label. Selecting one hands the category up; the composer's
 * "select what to promote" picker then shows that category's items. Copy stays inline (literals) so the
 * i18n extractor harvests it. See `video-subjects.ts` and the spec.
 */
export const VideoSubjectPicker = ({
  onPick,
  onClose,
  subjectTypes = OFFERED_SUBJECT_TYPES,
}: VideoSubjectPickerProps): React.ReactElement => {
  const { closeHandle } = useSheetDismiss(onClose);
  return (
    <Sheet title="What do you want to advertise?" onClose={onClose} closeHandle={closeHandle} maxWidth="1040px">
      <S.Grid $count={subjectTypes.length}>
        {subjectTypes.map((type) => (
          <S.Card key={type} type="button" onClick={() => onPick(type)}>
            <S.Art src={SUBJECT_ART[type]} alt="" aria-hidden="true" />
            <S.Label>
              {type === 'promotion' ? (
                <>
                  <S.Name text="Promotions" />
                  <S.Desc text="Welcome bonuses, cash drops, drops & wins." />
                </>
              ) : type === 'game' ? (
                <>
                  <S.Name text="Games" />
                  <S.Desc text="Any game, list or provider." />
                </>
              ) : (
                <>
                  <S.Name text="Gamification" />
                  <S.Desc text="Calendars, tournaments, rewards." />
                </>
              )}
            </S.Label>
          </S.Card>
        ))}
      </S.Grid>
    </Sheet>
  );
};

export default VideoSubjectPicker;
